/**
 * ZoneLabelOverlay
 *
 * Side-column label callouts with leader lines for Presentation mode.
 * Replaces the centroid-card markers that overlapped in dense zone
 * clusters. Each visible zone gets a label sitting in either the left or
 * right column (whichever side its centroid is closer to), and a thin
 * leader line connects the label to the zone's geographic centroid.
 *
 * The overlay renders as a child of the Leaflet map container so
 * html2canvas captures it along with the map tiles when exporting a PNG.
 *
 * Why this layout: with centroid-anchored cards, two adjacent zones whose
 * centroids are within ~80px (common in metro areas) collide. Labels on a
 * fixed vertical step in a side column NEVER overlap regardless of zone
 * density, and leader lines tell the eye exactly which polygon each
 * label belongs to. This is the same convention used by Mapbox Studio's
 * print mode, ESRI Maplex, and real-estate territory reports.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useMap } from 'react-leaflet';
import { pointOnFeature } from '@turf/point-on-feature';
import type * as GeoJSON from 'geojson';
import type { Zone } from '@vehix/portal/services/api/tenants';
import { getZoneSizeLabel } from '../utils/zoneStatistics';
import {
  ASSIGNED_ZONE_COLOR,
  AVAILABLE_ZONE_COLOR,
  VEHIX_ZONE_PALETTE,
  ZONE_AVAILABILITY_BADGE
} from '../utils/oemColors';

// Label box dimensions used for collision detection. Height is approximate —
// recall-line toggles change actual height by ~14px per line — but the
// detection tolerates over-estimates (treats labels as slightly bigger than
// they really are, which only adds a tiny bit of unnecessary spacing).
const LABEL_WIDTH = 200; // px
const LABEL_HEIGHT = 96; // px (worst-case: availability badge + both recall lines visible)
const LABEL_GAP = 6; // px minimum gap between adjacent labels

// Candidate offsets from zone centroid, ordered by preference. The first
// position that doesn't collide with an already-placed label wins. The
// preferred placement is "right of and slightly above the centroid" because
// reading order is left-to-right top-to-bottom — labels to the upper-right
// of their feature feel like a natural call-out.
const PLACEMENT_CANDIDATES: ReadonlyArray<{ dx: number; dy: number }> = [
  { dx: 24, dy: -LABEL_HEIGHT / 2 }, // right of centroid, vertically centered
  { dx: -LABEL_WIDTH - 24, dy: -LABEL_HEIGHT / 2 }, // left of centroid
  { dx: 24, dy: -LABEL_HEIGHT - 18 }, // upper-right
  { dx: -LABEL_WIDTH - 24, dy: -LABEL_HEIGHT - 18 }, // upper-left
  { dx: 24, dy: 18 }, // lower-right
  { dx: -LABEL_WIDTH - 24, dy: 18 }, // lower-left
  { dx: -LABEL_WIDTH / 2, dy: -LABEL_HEIGHT - 24 }, // directly above
  { dx: -LABEL_WIDTH / 2, dy: 24 }, // directly below
  { dx: 60, dy: -LABEL_HEIGHT / 2 }, // farther right
  { dx: -LABEL_WIDTH - 60, dy: -LABEL_HEIGHT / 2 } // farther left
];

interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

function boxesOverlap(a: Box, b: Box): boolean {
  if (a.x + a.width + LABEL_GAP < b.x) return false;
  if (b.x + b.width + LABEL_GAP < a.x) return false;
  if (a.y + a.height + LABEL_GAP < b.y) return false;
  if (b.y + b.height + LABEL_GAP < a.y) return false;
  return true;
}

interface ZoneLabelOverlayProps {
  zones: Zone[];
  /** zone_id → palette index (from adjacency-aware coloring) */
  zoneColorAssignment?: Map<string, number>;
  /** zone_id → current recall count (from useCurrentRecallCounts) */
  recallCountsNow?: Record<string, number>;
  /** zone_id → tenant name. Absent zones are available; omit while loading. */
  tenantByZone?: Record<string, string>;
  showNowRecalls: boolean;
  showCreationRecalls: boolean;
  /** When false, assigned zones say only "Assigned", without the tenant. */
  showTenantNames: boolean;
  /** Zones in the user's selection. Empty array = all visible zones get labels. */
  selectedZoneIds: string[];
}

interface LabelPlacement {
  zoneId: string;
  zoneName: string;
  sizeLabel: string;
  zipCount: number;
  recallsNow: number | undefined;
  recallsAtCreation: number;
  /** undefined while tenant mappings load — the availability line is hidden until then. */
  availability: { assigned: false } | { assigned: true; tenantName: string } | undefined;
  fillColor: string;
  strokeColor: string;
  centroidX: number; // pixel
  centroidY: number; // pixel
  labelX: number; // pixel — label container's left edge
  labelY: number; // pixel — label container's top edge
}

function getAvailability(
  tenantByZone: Record<string, string> | undefined,
  zoneId: string
): LabelPlacement['availability'] {
  if (!tenantByZone) return undefined;
  const tenantName = tenantByZone[zoneId];
  return tenantName ? { assigned: true, tenantName } : { assigned: false };
}

/** Assigned zones take the same slate as their polygon so label and zone match. */
function getLabelPalette(
  assigned: boolean,
  paletteIndex = 1
): { readonly fill: string; readonly stroke: string } {
  if (assigned) return ASSIGNED_ZONE_COLOR;
  return VEHIX_ZONE_PALETTE[paletteIndex % VEHIX_ZONE_PALETTE.length] ?? AVAILABLE_ZONE_COLOR;
}

export const ZoneLabelOverlay: React.FC<ZoneLabelOverlayProps> = ({
  zones,
  zoneColorAssignment,
  recallCountsNow,
  tenantByZone,
  showNowRecalls,
  showCreationRecalls,
  showTenantNames,
  selectedZoneIds
}) => {
  const map = useMap();

  // Track the map size so leader lines stay anchored when the user resizes
  // or enters/exits fullscreen. We intentionally re-trigger the layout on
  // every move/zoom because zone screen-positions change with the viewport.
  const [layoutTick, setLayoutTick] = useState(0);

  useEffect(() => {
    if (!map) return;
    const handler = () => setLayoutTick(t => t + 1);
    map.on('move', handler);
    map.on('zoom', handler);
    map.on('resize', handler);
    return () => {
      map.off('move', handler);
      map.off('zoom', handler);
      map.off('resize', handler);
    };
  }, [map]);

  const placements: LabelPlacement[] = useMemo(() => {
    if (!map) return [];
    void layoutTick; // ensure recompute on map move

    const container = map.getContainer();
    const mapWidth = container.clientWidth;
    const mapHeight = container.clientHeight;

    const selectedSet = new Set(selectedZoneIds);
    const showAll = selectedSet.size === 0;

    // Step 1: compute per-zone screen-pixel centroid for every zone we'll
    // try to label.
    type Pre = Omit<LabelPlacement, 'labelX' | 'labelY'>;
    const pre: Pre[] = [];
    zones.forEach(zone => {
      if (!showAll && !selectedSet.has(zone.id)) return;
      const geometry = (zone.metadata as Record<string, unknown> | undefined)?.['geometry'] as
        | GeoJSON.Geometry
        | undefined;
      if (!geometry) return;
      let lat: number;
      let lng: number;
      try {
        const point = pointOnFeature({
          type: 'Feature',
          geometry,
          properties: {}
        } as GeoJSON.Feature<GeoJSON.Geometry>);
        const coords = (point.geometry as GeoJSON.Point).coordinates;
        lng = coords[0]!;
        lat = coords[1]!;
      } catch {
        return;
      }
      const pixel = map.latLngToContainerPoint([lat, lng]);
      // Skip zones whose centroid is off-screen — labeling them would point
      // a leader line off the visible map area.
      if (pixel.x < 0 || pixel.y < 0 || pixel.x > mapWidth || pixel.y > mapHeight) {
        return;
      }
      const availability = getAvailability(tenantByZone, zone.id);
      const palette = getLabelPalette(!!availability?.assigned, zoneColorAssignment?.get(zone.id));
      pre.push({
        zoneId: zone.id,
        zoneName: zone.zone_name || 'Zone',
        // Size describes the zone now, not when it was drawn. 'customer' because this
        // overlay only ever mounts in Presentation mode, where the ladder caps at Large.
        sizeLabel: getZoneSizeLabel(
          recallCountsNow?.[zone.id] ?? zone.total_recalls_current ?? 0,
          zone.oem,
          'customer'
        ),
        zipCount: zone.zip_code_count ?? zone.zip_codes?.length ?? 0,
        recallsNow: recallCountsNow?.[zone.id],
        recallsAtCreation: zone.total_recalls_at_creation ?? 0,
        availability,
        fillColor: palette.fill,
        strokeColor: palette.stroke,
        centroidX: pixel.x,
        centroidY: pixel.y
      });
    });

    // Step 2: greedy nearby placement with collision avoidance. Sort by
    // centroid Y so labels are placed top-to-bottom (matches natural reading
    // order; the FIRST label gets the most-preferred position, later labels
    // adapt around it).
    const sorted = [...pre].sort((a, b) => a.centroidY - b.centroidY);
    const placed: LabelPlacement[] = [];
    const placedBoxes: Box[] = [];

    sorted.forEach(p => {
      let chosenX = p.centroidX + PLACEMENT_CANDIDATES[0]!.dx;
      let chosenY = p.centroidY + PLACEMENT_CANDIDATES[0]!.dy;

      // Try each candidate offset; first non-overlapping one wins. If all
      // candidates collide, fall back to the most-preferred offset and let
      // it overlap (rare with ~10 zones).
      for (const cand of PLACEMENT_CANDIDATES) {
        const candX = p.centroidX + cand.dx;
        const candY = p.centroidY + cand.dy;
        const candBox: Box = {
          x: candX,
          y: candY,
          width: LABEL_WIDTH,
          height: LABEL_HEIGHT
        };

        // Reject candidates that would push the label off the map edges.
        if (candBox.x < 4) continue;
        if (candBox.y < 4) continue;
        if (candBox.x + candBox.width > mapWidth - 4) continue;
        if (candBox.y + candBox.height > mapHeight - 4) continue;

        // Reject candidates that overlap any already-placed label.
        const collides = placedBoxes.some(b => boxesOverlap(b, candBox));
        if (collides) continue;

        chosenX = candX;
        chosenY = candY;
        break;
      }

      const finalBox: Box = {
        x: chosenX,
        y: chosenY,
        width: LABEL_WIDTH,
        height: LABEL_HEIGHT
      };
      placedBoxes.push(finalBox);
      placed.push({
        ...p,
        labelX: chosenX,
        labelY: chosenY
      });
    });

    return placed;
  }, [map, zones, zoneColorAssignment, recallCountsNow, tenantByZone, selectedZoneIds, layoutTick]);

  if (placements.length === 0) return null;

  return (
    <div
      className='zone-label-overlay'
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 500 // above polygons (~400) but below floating filter overlay (1000)
      }}
    >
      {/* Leader lines — SVG sized to the map container. */}
      <svg
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none'
        }}
      >
        {placements.map(p => {
          // Anchor the leader line to whichever edge of the label box is
          // closest to the centroid. With the nearby-zone placement strategy,
          // labels can sit on any side of their zone, so we pick the
          // attachment point dynamically rather than assuming left/right.
          const labelCenterX = p.labelX + LABEL_WIDTH / 2;
          const labelCenterY = p.labelY + LABEL_HEIGHT / 2;
          const dx = p.centroidX - labelCenterX;
          const dy = p.centroidY - labelCenterY;
          let anchorX: number;
          let anchorY: number;
          if (Math.abs(dx) >= Math.abs(dy)) {
            // Centroid is more to the side than above/below — anchor to the
            // left or right edge of the label.
            anchorX = dx >= 0 ? p.labelX + LABEL_WIDTH : p.labelX;
            anchorY = labelCenterY;
          } else {
            // Centroid is above or below — anchor to the top/bottom edge.
            anchorX = labelCenterX;
            anchorY = dy >= 0 ? p.labelY + LABEL_HEIGHT : p.labelY;
          }
          // Slight curve for polish — quadratic bezier with the control
          // point biased toward the label so the line "hangs" naturally.
          const ctrlX = (anchorX + p.centroidX) / 2;
          const ctrlY = anchorY;
          return (
            <g key={p.zoneId}>
              <path
                d={`M ${anchorX},${anchorY} Q ${ctrlX},${ctrlY} ${p.centroidX},${p.centroidY}`}
                fill='none'
                stroke={p.strokeColor}
                strokeWidth={1.25}
                opacity={0.7}
              />
              <circle
                cx={p.centroidX}
                cy={p.centroidY}
                r={3.5}
                fill={p.fillColor}
                stroke={p.strokeColor}
                strokeWidth={1}
              />
            </g>
          );
        })}
      </svg>

      {/* Labels — one absolutely-positioned card per zone. */}
      {placements.map(p => (
        <div
          key={p.zoneId}
          style={{
            position: 'absolute',
            left: `${p.labelX}px`,
            top: `${p.labelY}px`,
            width: `${LABEL_WIDTH}px`,
            background: 'rgba(255,255,255,0.97)',
            border: `1.5px solid ${p.strokeColor}`,
            borderLeft: `4px solid ${p.fillColor}`,
            borderRadius: '8px',
            padding: '6px 10px',
            boxShadow: '0 4px 12px rgba(15,23,42,0.18)',
            fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
            pointerEvents: 'none'
          }}
        >
          <div
            style={{
              fontSize: '12.5px',
              fontWeight: 600,
              color: '#0f172a',
              lineHeight: 1.2
            }}
          >
            {p.zoneName}
          </div>
          {p.availability && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '3px',
                minWidth: 0
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  padding: '1px 7px',
                  borderRadius: '999px',
                  fontSize: '10.5px',
                  fontWeight: 600,
                  lineHeight: '16px',
                  ...ZONE_AVAILABILITY_BADGE[p.availability.assigned ? 'assigned' : 'available']
                }}
              >
                {p.availability.assigned ? 'Assigned' : 'Available'}
              </span>
              {p.availability.assigned && showTenantNames && (
                <span
                  style={{
                    fontSize: '11px',
                    color: '#475569',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {p.availability.tenantName}
                </span>
              )}
            </div>
          )}
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
            {p.sizeLabel} · {p.zipCount} ZIP{p.zipCount === 1 ? '' : 's'}
          </div>
          {showNowRecalls && p.recallsNow !== undefined && (
            <div style={{ fontSize: '11px', color: '#0f172a', marginTop: '2px' }}>
              Current: <strong>{p.recallsNow.toLocaleString()}</strong> recalls
            </div>
          )}
          {showCreationRecalls && (
            <div
              style={{
                fontSize: '11px',
                color: '#475569',
                marginTop: '1px'
              }}
            >
              At creation: <strong>{p.recallsAtCreation.toLocaleString()}</strong> recalls
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

ZoneLabelOverlay.displayName = 'ZoneLabelOverlay';
